import type { WizardStep as UIWizardStep } from "@sentricmusic/ui";

import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Heading, IconInfo, IconRestartList, Text, WizardNavigation, WizardProgress } from "@sentricmusic/ui";
import { useRouterState } from "@tanstack/react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import RebrandButtonLink from "@/components/RebrandButtonLink";
// @ts-expect-error - Legacy JSX component without types
import useSongsAddAlbumTour from "@/components/tour/SongsAddAlbumTour";
import PlaylistImportStep from "@/features/songs/playlist/steps/PlaylistImportStep";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import type { SourceType } from "./store/multiSongStore";

import { useNavigationGuard } from "./hooks/useNavigationGuard";
import AlbumImportStep from "./steps/AlbumImportStep";
import AssignWritersStep from "./steps/AssignWritersStep";
import ChooseWritersStep from "./steps/ChooseWritersStep";
import DetailsAndSharesStep from "./steps/DetailsAndSharesStep";
import SelectSongsStep from "./steps/SelectSongsStep";
import SummaryAndSubmitStep from "./steps/SummaryAndSubmitStep";
import useMultiSongStore from "./store/multiSongStore";
import { useWizardStore } from "./store/wizardStore";
import useWriterStore from "./store/writerStore";
import { validateAllSongsHaveWriters } from "./utils/validation";

export type SongsAddMultipleProps = {
  /** Whether the wizard is in "album" or "playlist" mode. */
  source: SourceType;
};

// step descriptor to pass into Wizard
const stepOrder = [
  "import",
  "select-songs",
  "choose-writers",
  "assign-writers",
  "details-and-shares",
  "summary-and-submit",
] as const;

type LocalWizardStep = typeof stepOrder[number];

/**
 * Shared multi-song wizard orchestrator.
 *
 * Steps 2-6 (select songs, choose writers, assign writers, details & shares,
 * summary & submit) are 100% source-agnostic. Only Step 1 differs:
 *   - album  → AlbumImportStep
 *   - playlist → PlaylistImportStep
 */
export default function SongsAddMultiple({ source }: SongsAddMultipleProps) {
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const selectedAccount = useAppStore(state => state.selectedAccount) as string;
  const flags = useFlags();
  const newAddSongs = flags["pd-add-songs-rework"] as boolean;
  const showTourFlag = flags["pd-show-tutorial"] as boolean;
  const location = useRouterState({ select: s => s.location });
  const { t } = useTranslation(["album", "playlist", "tutorial", "songs"]);

  const WIZARD_STEPS: UIWizardStep[] = useMemo(() => [
    { id: "import", title: t("album:step-label-import", "Import") },
    { id: "select-songs", title: t("album:step-label-select-songs", "Select Songs") },
    { id: "choose-writers", title: t("album:step-label-choose-writers", "Choose Writers") },
    { id: "assign-writers", title: t("album:step-label-assign-writers", "Assign Writers") },
    { id: "details-and-shares", title: t("album:step-label-details-and-shares", "Details & Shares") },
    { id: "summary-and-submit", title: t("album:step-label-summary-and-submit", "Summary & Submit") },
  ], [t]);

  // eslint-disable-next-line ts/no-unsafe-call
  const rawStartTour: unknown = useSongsAddAlbumTour();
  const startTour: () => void = typeof rawStartTour === "function" ? (rawStartTour as () => void) : () => {};

  // --- Stores ---
  const multiSongStore = useMultiSongStore();
  const { currentStep, markStepComplete, nextStep, goToStep, previousStep, completedSteps, canNavigateToStep } = useWizardStore();
  const { selectedWriters, clearSelectedWriters } = useWriterStore();
  const { tracks, isLoading, error: errorMsg, sourceData } = multiSongStore;

  // --- Navigation guard (unsaved changes) ---
  const { showDialog: showLeaveDialog, onConfirmLeave, onCancelLeave } = useNavigationGuard(sourceData !== null);

  // Set sourceType on mount / when source prop changes
  useEffect(() => {
    multiSongStore.setSourceType(source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  // --- Account change detection ---
  const initialAccount = useRef<string | null>(null);

  // Clear writer store on mount
  useEffect(() => {
    clearSelectedWriters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset wizard when account changes
  useEffect(() => {
    if (selectedAccount === null || selectedAccount === "" || selectedAccount === undefined)
      return;

    if (initialAccount.current === null) {
      initialAccount.current = selectedAccount;
      return;
    }

    if (initialAccount.current !== selectedAccount) {
      multiSongStore.clearState();
      clearSelectedWriters();
      useWizardStore.getState().resetWizard();
      initialAccount.current = selectedAccount;
      toast.info(
        source === "playlist"
          ? t("playlist:wizard-reset-account-change", "Playlist wizard reset due to account change")
          : t("album:wizard-reset-account-change", "Album wizard reset due to account change"),
      );
    }
  }, [selectedAccount, multiSongStore, clearSelectedWriters, source, t]);

  // --- Reset handler ---
  const handleResetWizard = () => {
    multiSongStore.clearState();
    clearSelectedWriters();
    useWizardStore.getState().resetWizard();

    // Clear deep-link URL params if present
    const searchParams = new URLSearchParams(location.searchStr);
    const deepLinkParam = source === "playlist" ? "spotifyPlaylistId" : "spotifyAlbumId";
    if (searchParams.has(deepLinkParam)) {
      searchParams.delete(deepLinkParam);
      const newUrl = `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
      window.history.replaceState({}, "", newUrl);
    }

    toast.success(
      source === "playlist"
        ? t("playlist:wizard-reset", "Playlist wizard has been reset")
        : t("album:wizard-reset", "Album wizard has been reset"),
    );
  };

  // --- Step completion handlers ---
  const handleImportComplete = () => {
    markStepComplete("import");
    nextStep();
  };

  const handleSelectSongsComplete = () => {
    markStepComplete("select-songs");

    const selectedTracks = tracks.filter(t => t.isSelected);
    const allTracksAreRecordingsOrVariants = selectedTracks.every((track) => {
      const isRecording = track.isSuspectedDuplicate === true && track.reviewAction === "attach-recording";
      const isVariant = typeof track.suspectedAlbumTrackId === "string"
        && track.suspectedAlbumTrackId.length > 0
        && track.reviewAction !== "dismiss";
      return isRecording || isVariant;
    });

    if (allTracksAreRecordingsOrVariants && selectedTracks.length > 0) {
      markStepComplete("choose-writers");
      markStepComplete("assign-writers");
      goToStep("details-and-shares");
    }
    else {
      nextStep();
    }
  };

  const handleChooseWritersComplete = () => {
    markStepComplete("choose-writers");
    nextStep();
  };

  const handleAssignWritersComplete = () => {
    const validation = validateAllSongsHaveWriters();
    if (!validation.isValid) {
      toast.error(`Cannot proceed: ${validation.errorMessage}`);
      return;
    }
    markStepComplete("assign-writers");
    nextStep();
  };

  const handleDetailsAndSharesComplete = () => {
    markStepComplete("details-and-shares");
    nextStep();
  };

  // --- Summary & submit state ---
  const [summaryAndSubmitValid, setSummaryAndSubmitValid] = useState(false);
  const [summaryAndSubmitFn, setSummaryAndSubmitFn] = useState<(() => Promise<void>) | null>(null);

  const handleSummaryAndSubmitValidation = (isValid: boolean, submitFn?: () => Promise<void>) => {
    setSummaryAndSubmitValid(isValid);
    setSummaryAndSubmitFn(() => submitFn || null);
  };

  const handleSummaryAndSubmitComplete = async () => {
    markStepComplete("summary-and-submit");
  };

  const currentStepIndex = Math.max(0, stepOrder.indexOf(currentStep as LocalWizardStep));
  const stepsWithCompletion = WIZARD_STEPS.map((s) => {
    return {
      ...s,
      isComplete: completedSteps.has(s.id as LocalWizardStep),
    };
  });

  // --- i18n helpers ---

  const pageTitle = source === "playlist"
    ? t("playlist:add-playlist", "Add Playlist")
    : t("album:add-album", "Add Album");

  const importStepTitle = source === "playlist"
    ? t("playlist:step-1-import-playlist", "Step 1: Import Playlist")
    : t("album:step-1-import-album", "Step 1: Import Album");

  const importStepDescription = source === "playlist"
    ? t("playlist:connect-spotify-import-playlist", "Connect to Spotify to import your playlist")
    : t("album:connect-spotify-import-album", "Connect to Spotify to import your album");

  // --- Start Over button (reused across steps) ---
  const renderResetButton = () => (
    <Button
      type="button"
      variant="link"
      onClick={handleResetWizard}
      tooltip
      tooltipText={t("songs:start-over-tooltip", "Start Over")}
      icon={<IconRestartList size="lg" className={`${isTunecore ? "stroke-white group-hover:stroke-ink-accent" : "group-hover:stroke-action-primary"}`} />}
    />
  );

  // --- Render current step ---
  const renderStepContent = () => {
    switch (currentStep) {
      case "import":
        return (
          <>
            <div className="flex items-center justify-between">
              <div>
                <Heading as="h3">{importStepTitle}</Heading>
                <Text as="p">{importStepDescription}</Text>
              </div>
              {renderResetButton()}
            </div>

            {source === "playlist" ? <PlaylistImportStep /> : <AlbumImportStep />}

            <WizardNavigation
              isTunecore={isTunecore}
              canGoNext={!(!sourceData || tracks.length === 0 || isLoading || (errorMsg !== null && errorMsg !== ""))}
              onNext={handleImportComplete}
              hidePrevious={true}
              nextLabel={t("album:continue-to-select-songs", "Continue to Select Songs")}
            />
          </>
        );

      case "select-songs": {
        const unreviewedCount = tracks.filter(t =>
          t.isSuspectedDuplicate
          && !t.isDuplicate
          && t.isSelected
          && t.reviewAction === null,
        ).length;

        return (
          <>
            <div className="flex items-center justify-between">
              <div>
                <Heading as="h3">{t("album:step-2-select-songs", "Step 2: Select Songs")}</Heading>
                <Text as="p">{t("album:choose-tracks-to-add", "Choose which songs you want to add to your catalogue.")}</Text>
              </div>
              {renderResetButton()}
            </div>

            <SelectSongsStep />

            <WizardNavigation
              isTunecore={isTunecore}
              onPrevious={previousStep}
              canGoNext={!(isLoading || tracks.filter(t => t.isSelected).length === 0 || unreviewedCount > 0)}
              onNext={handleSelectSongsComplete}
              nextLabel={t("album:continue-to-choose-writers", "Continue to Choose Writers")}
            />
          </>
        );
      }

      case "choose-writers":
        return (
          <>
            <div className="flex items-center justify-between">
              <div>
                <Heading as="h3">{t("album:step-3-choose-writers", "Step 3: Choose Writers")}</Heading>
                <Text as="p">{t("album:select-create-writers", "Either select an existing writer or create a new writer.")}</Text>
              </div>
              {renderResetButton()}
            </div>

            <ChooseWritersStep />

            <WizardNavigation
              isTunecore={isTunecore}
              onPrevious={previousStep}
              canGoNext={selectedWriters.length > 0}
              onNext={handleChooseWritersComplete}
              nextLabel={t("album:continue-to-assign-writers", "Continue to Assign Writers")}
            />
          </>
        );

      case "assign-writers":
        return (
          <>
            <div className="flex items-center justify-between">
              <div>
                <Heading as="h3">{t("album:step-4-assign-writers", "Step 4: Assign Writers to Songs")}</Heading>
                <Text as="p">{t("album:assign-selected-writers-each-song", "Assign the selected writers to each song.")}</Text>
              </div>
              {renderResetButton()}
            </div>

            <AssignWritersStep />

            <WizardNavigation
              isTunecore={isTunecore}
              onPrevious={previousStep}
              canGoNext={true}
              onNext={handleAssignWritersComplete}
              nextLabel={t("album:continue-to-song-details-shares", "Continue to Song Details & Shares")}
            />
          </>
        );

      case "details-and-shares":
        return (
          <>
            <div className="flex items-center justify-between">
              <div>
                <Heading as="h3">{t("album:step-5-song-details-shares", "Step 5: Song Details & Writer Shares")}</Heading>
                <Text as="p">{t("album:set-song-details-publishing-shares", "Complete song details and publishing shares for each writer, on each song.")}</Text>
              </div>
              {renderResetButton()}
            </div>

            <DetailsAndSharesStep />

            <WizardNavigation
              isTunecore={isTunecore}
              onPrevious={previousStep}
              canGoNext={true}
              onNext={handleDetailsAndSharesComplete}
              nextLabel={t("album:continue-to-summary", "Continue to Summary")}
            />
          </>
        );

      case "summary-and-submit":
        return (
          <>
            <div className="flex items-center justify-between">
              <div>
                <Heading as="h3">{t("album:step-6-review-submit", "Step 6: Review & Submit")}</Heading>
                <Text as="p">{t("album:review-song-details-submit", "Review all song details and submit to create your song registrations.")}</Text>
              </div>
              {renderResetButton()}
            </div>

            <SummaryAndSubmitStep source={source} onValidationComplete={handleSummaryAndSubmitValidation} />

            <WizardNavigation
              isTunecore={isTunecore}
              onPrevious={previousStep}
              canGoNext={summaryAndSubmitValid}
              isLastStep={true}
              onNext={async () => {
                if (summaryAndSubmitFn) {
                  try {
                    await summaryAndSubmitFn();
                    await handleSummaryAndSubmitComplete();
                  }
                  catch (error) {
                    console.error(`Error during ${source} submission:`, error);
                  }
                }
                else {
                  console.error("Submission function not available");
                }
              }}
              nextLabel={t("album:create-all-songs", "Create All Songs")}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex flex-wrap w-full lg:min-h-screen-minus-header">
        <div
          className={`w-full ${
            typeof location !== "undefined" && location.pathname === "/songs" ? "lg:p-6 p-4" : ""
          } ${
            newAddSongs
              ? ""
              : typeof location !== "undefined" && (location.pathname === "/songs/create" || location.pathname === "/songs/create-album" || location.pathname === "/songs/create-playlist")
                ? "lg:min-h-screen-minus-header mx-4 lg:mx-12 xl:mx-20 2xl:mx-40 my-12"
                : "lg:p-6 p-4 lg:min-h-screen-minus-header"
          } ${isTunecore ? "text-white" : ""}`}
        >
          <div className="flex flex-row gap-4 mb-8">
            <Heading className="flex items-end justify-between gap-5 font-semibold" as="h3">
              {pageTitle}
              {showTourFlag && source === "album" && (
                <RebrandButtonLink onClick={startTour}>
                  <div className="flex items-center gap-2 mb-1 font-normal">
                    {t("tutorial:need-help")}
                    {" "}
                    <IconInfo className="w-5 h-5 mb-1 stroke-action-primary" />
                  </div>
                </RebrandButtonLink>
              )}
            </Heading>
          </div>

          {/* Main Content Area */}
          <div className={`flex flex-col gap-4 ${isTunecore ? "bg-layer-inverse" : "bg-white"} rounded-lg p-6 shadow-md`}>
            <WizardProgress
              steps={stepsWithCompletion}
              currentStepIndex={currentStepIndex}
              isTunecore={isTunecore}
              onStepClick={(index: number) => {
                const stepId = stepOrder[index];
                if (stepId != null)
                  goToStep(stepId);
              }}
              canNavigateToStep={(index: number) => {
                const stepId = stepOrder[index];
                return stepId != null && canNavigateToStep(stepId);
              }}
            />
            {renderStepContent()}
          </div>
        </div>
      </div>

      {/* Unsaved changes confirmation dialog */}
      <Dialog
        open={showLeaveDialog}
        onOpenChange={(open: boolean) => {
          if (!open)
            onCancelLeave();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("songs:leave-wizard-title", "Leave this page?")}
            </DialogTitle>
            <DialogDescription>
              {t("songs:leave-wizard-description", "You have unsaved progress in the wizard. If you leave now, your changes will be lost.")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-4 sm:gap-0">
            <Button
              variant="primary"
              label={t("songs:stay-on-page", "Stay")}
              onClick={onCancelLeave}
            />
            <Button
              variant="secondary"
              label={t("songs:leave-page", "Leave")}
              onClick={() => {
                multiSongStore.clearState();
                clearSelectedWriters();
                useWizardStore.getState().resetWizard();
                onConfirmLeave();
              }}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
